// src/services/reportsService.js
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  or,
  arrayUnion,
  arrayRemove,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { db, auth, storage, COLLECTIONS } from './firebase';
import { AssessmentService } from './taskService';
import { StatisticsCalculator } from '../utils/statistics';
import { generateResponse } from '../utils/geminiApi';
import { TaskAnalysisService } from './taskAnalysisService';

export class ReportsService {
  // Generate Grade Overall Report
  static async generateGradeReport(grade, userProfile) {
    try {
      const allTasks = await AssessmentService.getAssessmentTasks();
      const gradeTasks = allTasks.filter(task => task.grade === grade);

      const allMarks = [];
      const learnerSchoolMap = new Map(); // Track learner-school relationships
      
      for (const task of gradeTasks) {
        try {
          const marks = await AssessmentService.getLearnerMarks(task.id);
          
          // Build learner-school mapping
          marks.forEach(mark => {
            const learnerKey = `${mark.learnerSurname}|${mark.learnerName}`;
            if (mark.school && !learnerSchoolMap.has(learnerKey)) {
              learnerSchoolMap.set(learnerKey, mark.school);
            }
          });
          
          allMarks.push(...marks);
        } catch (error) {
          console.warn(`Error loading marks for task ${task.id}:`, error);
        }
      }

      if (allMarks.length === 0) {
        throw new Error(`No assessment data found for Grade ${grade}`);
      }

      const stats = this.calculateGradeStatistics(allMarks, gradeTasks);
      
      // Enhanced learner breakdown with schools
      const learnerBreakdown = this.generateLearnerSchoolBreakdown(allMarks, learnerSchoolMap);
      
      const prompt = this.buildEnhancedGradeReportPrompt(
        grade, stats, allMarks, gradeTasks, learnerBreakdown, userProfile
      );
      
      const aiAnalysis = await generateResponse(prompt);

      return {
        type: 'grade',
        grade,
        generatedAt: new Date(),
        stats: {
          ...stats,
          learnerBreakdown, // Include school breakdown
          schoolSummary: this.generateSchoolSummary(learnerBreakdown)
        },
        aiAnalysis,
        rawData: {
          totalLearners: stats.totalLearners,
          schools: learnerBreakdown.schoolCount,
          averagePerformance: stats.fiveNumberSummary.mean
        }
      };
    } catch (error) {
      console.error('Error generating grade report:', error);
      throw error;
    }
  }

  static generateLearnerSchoolBreakdown(marks, learnerSchoolMap) {
    const schoolBreakdown = new Map();
    const learnerPerformance = new Map();
    
    marks.forEach(mark => {
      const learnerKey = `${mark.learnerSurname}|${mark.learnerName}`;
      const school = learnerSchoolMap.get(learnerKey) || 'School Not Specified';
      
      // Track school performance
      if (!schoolBreakdown.has(school)) {
        schoolBreakdown.set(school, {
          schoolName: school,
          learners: new Set(),
          totalMarks: [],
          averagePerformance: 0,
          learnerList: []
        });
      }
      
      const schoolData = schoolBreakdown.get(school);
      schoolData.learners.add(learnerKey);
      schoolData.totalMarks.push(mark.percentageMark);
      
      // Track individual learner performance
      if (!learnerPerformance.has(learnerKey)) {
        learnerPerformance.set(learnerKey, {
          surname: mark.learnerSurname,
          name: mark.learnerName,
          school: school,
          marks: [],
          averagePerformance: 0
        });
      }
      
      learnerPerformance.get(learnerKey).marks.push(mark.percentageMark);
    });
    
    // Calculate averages
    schoolBreakdown.forEach((schoolData, school) => {
      schoolData.averagePerformance = schoolData.totalMarks.reduce((a, b) => a + b, 0) / schoolData.totalMarks.length;
      schoolData.learnerCount = schoolData.learners.size;
      
      // Generate learner list for this school
      schoolData.learnerList = Array.from(schoolData.learners).map(learnerKey => {
        const learner = learnerPerformance.get(learnerKey);
        learner.averagePerformance = learner.marks.reduce((a, b) => a + b, 0) / learner.marks.length;
        return learner;
      }).sort((a, b) => a.surname.localeCompare(b.surname));
    });
    
    return {
      schools: Array.from(schoolBreakdown.values()).sort((a, b) => a.schoolName.localeCompare(b.schoolName)),
      schoolCount: schoolBreakdown.size,
      totalLearners: learnerPerformance.size,
      learnersBySchool: Array.from(learnerPerformance.values()).sort((a, b) => a.surname.localeCompare(b.surname))
    };
  }

  static generateSchoolSummary(learnerBreakdown) {
    return {
      totalSchools: learnerBreakdown.schoolCount,
      schoolPerformanceRanking: learnerBreakdown.schools
        .sort((a, b) => b.averagePerformance - a.averagePerformance)
        .map((school, index) => ({
          rank: index + 1,
          name: school.schoolName,
          learnerCount: school.learnerCount,
          averagePerformance: school.averagePerformance
        })),
      topPerformingSchool: learnerBreakdown.schools.reduce((top, current) => 
        current.averagePerformance > top.averagePerformance ? current : top
      ),
      schoolDistribution: learnerBreakdown.schools.map(school => ({
        name: school.schoolName,
        percentage: (school.learnerCount / learnerBreakdown.totalLearners * 100).toFixed(1)
      }))
    };
  }

  // Generate Subject Report (per grade)
  static async generateSubjectReport(grade, subject, userProfile, context = '') {
    try {
      const allTasks = await AssessmentService.getAssessmentTasks();
      const subjectTasks = allTasks.filter(task => 
        task.grade === grade && task.subject === subject
      );
      
      if (subjectTasks.length === 0) {
        throw new Error(`No assessment tasks found for Grade ${grade} ${subject}`);
      }

      // Extract task structures for detailed analysis
      const taskStructures = subjectTasks.map(task => ({
        task,
        structure: TaskAnalysisService.finalizeBreakdowns(
          TaskAnalysisService.extractTaskStructure(task)
        )
      }));

      const allSubmissions = [];
      const allMarks = [];
      
      for (const task of subjectTasks) {
        try {
          const submissions = await AssessmentService.getTaskSubmissions(task.id);
          const marks = await AssessmentService.getLearnerMarks(task.id);
          allSubmissions.push(...submissions);
          allMarks.push(...marks);
        } catch (error) {
          console.warn(`Error loading data for task ${task.id}:`, error);
        }
      }

      const stats = this.calculateEnhancedSubjectStatistics(taskStructures, allSubmissions, allMarks);
      const prompt = this.buildEnhancedSubjectReportPrompt(grade, subject, stats, taskStructures, allMarks, context, userProfile);
      const aiAnalysis = await generateResponse(prompt);

      return {
        type: 'subject',
        grade,
        subject,
        generatedAt: new Date(),
        stats,
        aiAnalysis,
        context,
        rawData: {
          tasks: subjectTasks.length,
          submissions: allSubmissions.length,
          marks: allMarks.length,
          taskStructures
        },
        chartData: this.generateEnhancedSubjectChartData(stats, taskStructures)
      };
    } catch (error) {
      console.error('Error generating enhanced subject report:', error);
      throw error;
    }
  }

  // Generate Individual Learner Report (per subject)
  static async generateLearnerSubjectReport(learnerSurname, learnerName, grade, subject, userProfile) {
    try {
      const allTasks = await AssessmentService.getAssessmentTasks();
      const subjectTasks = allTasks.filter(task => 
        task.grade === grade && task.subject === subject
      );

      const learnerMarks = [];
      for (const task of subjectTasks) {
        try {
          const marks = await AssessmentService.getLearnerMarks(task.id);
          const learnerTaskMarks = marks.filter(mark =>
            mark.learnerSurname === learnerSurname && mark.learnerName === learnerName
          );
          learnerMarks.push(...learnerTaskMarks);
        } catch (error) {
          console.warn(`Error loading marks for task ${task.id}:`, error);
        }
      }

      if (learnerMarks.length === 0) {
        throw new Error(`No marks found for ${learnerSurname}, ${learnerName} in Grade ${grade} ${subject}`);
      }

      const stats = this.calculateLearnerStatistics(learnerMarks, subjectTasks);
      const prompt = this.buildLearnerSubjectReportPrompt(
        learnerSurname, learnerName, grade, subject, stats, learnerMarks, subjectTasks, userProfile
      );
      const aiAnalysis = await generateResponse(prompt);

      return {
        type: 'learner-subject',
        learnerSurname,
        learnerName,
        grade,
        subject,
        generatedAt: new Date(),
        stats,
        aiAnalysis,
        rawData: {
          assessments: learnerMarks.length,
          tasks: subjectTasks.length,
          averagePerformance: stats.averagePerformance
        }
      };
    } catch (error) {
      console.error('Error generating learner subject report:', error);
      throw error;
    }
  }

  // Generate Individual Learner Overall Report
  static async generateLearnerOverallReport(learnerSurname, learnerName, grade, userProfile) {
    try {
      const allTasks = await AssessmentService.getAssessmentTasks();
      const gradeTasks = allTasks.filter(task => task.grade === grade);

      const learnerMarks = [];
      for (const task of gradeTasks) {
        try {
          const marks = await AssessmentService.getLearnerMarks(task.id);
          const learnerTaskMarks = marks.filter(mark =>
            mark.learnerSurname === learnerSurname && mark.learnerName === learnerName
          );
          learnerMarks.push(...learnerTaskMarks);
        } catch (error) {
          console.warn(`Error loading marks for task ${task.id}:`, error);
        }
      }

      if (learnerMarks.length === 0) {
        throw new Error(`No marks found for ${learnerSurname}, ${learnerName} in Grade ${grade}`);
      }

      const stats = this.calculateLearnerOverallStatistics(learnerMarks, gradeTasks);
      const prompt = this.buildLearnerOverallReportPrompt(
        learnerSurname, learnerName, grade, stats, learnerMarks, gradeTasks, userProfile
      );
      const aiAnalysis = await generateResponse(prompt);

      return {
        type: 'learner-overall',
        learnerSurname,
        learnerName,
        grade,
        generatedAt: new Date(),
        stats,
        aiAnalysis,
        rawData: {
          assessments: learnerMarks.length,
          subjects: [...new Set(learnerMarks.map(m => {
            const task = gradeTasks.find(t => t.id === m.taskId);
            return task?.subject;
          }).filter(Boolean))],
          averagePerformance: stats.overallAverage
        }
      };
    } catch (error) {
      console.error('Error generating learner overall report:', error);
      throw error;
    }
  }

  static async saveReport(report, title = null) {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('User not authenticated');

      const reportData = {
        ...report,
        title: title || `${report.type.replace('-', ' ')} Report`,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        // Add searchable fields
        searchableGrade: report.grade || '',
        searchableSubject: report.subject || '',
        searchableLearner: report.learnerSurname && report.learnerName 
          ? `${report.learnerSurname}, ${report.learnerName}` 
          : '',
        // Generate chart data for embedding
        chartData: this.generateChartDataForReport(report)
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.REPORTS), reportData);
      return { id: docRef.id, ...reportData };
    } catch (error) {
      console.error('Error saving report:', error);
      throw error;
    }
  }

  // Get saved reports
  static async getSavedReports(filters = {}) {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('User not authenticated');

      let q = query(
        collection(db, COLLECTIONS.REPORTS),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      let reports = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));

      // Apply client-side filters
      if (filters.grade) {
        reports = reports.filter(report => report.searchableGrade === filters.grade);
      }
      if (filters.subject) {
        reports = reports.filter(report => report.searchableSubject === filters.subject);
      }
      if (filters.learner) {
        reports = reports.filter(report => 
          report.searchableLearner.toLowerCase().includes(filters.learner.toLowerCase())
        );
      }
      if (filters.type) {
        reports = reports.filter(report => report.type === filters.type);
      }

      return reports;
    } catch (error) {
      console.error('Error fetching saved reports:', error);
      throw error;
    }
  }

  // Delete a saved report
  static async deleteReport(reportId) {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('User not authenticated');

      const reportRef = doc(db, COLLECTIONS.REPORTS, reportId);
      const reportDoc = await getDoc(reportRef);
      
      if (!reportDoc.exists()) {
        throw new Error('Report not found');
      }
      
      if (reportDoc.data().createdBy !== userId) {
        throw new Error('Permission denied');
      }

      await deleteDoc(reportRef);
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  }

  // Generate chart data for embedding in reports
  static generateChartDataForReport(report) {
    const charts = [];

    if (report.stats) {
      // Performance distribution chart
      if (report.stats.performanceLevels) {
        charts.push({
          type: 'doughnut',
          title: 'Performance Distribution',
          data: {
            labels: report.stats.performanceLevels.map(level => level.level),
            datasets: [{
              data: report.stats.performanceLevels.map(level => level.count),
              backgroundColor: [
                'rgba(34, 197, 94, 0.7)',
                'rgba(59, 130, 246, 0.7)',
                'rgba(234, 179, 8, 0.7)',
                'rgba(249, 115, 22, 0.7)',
                'rgba(236, 72, 153, 0.7)',
                'rgba(239, 68, 68, 0.7)',
              ]
            }]
          }
        });
      }

      // Subject averages chart
      if (report.stats.subjectAverages) {
        charts.push({
          type: 'bar',
          title: 'Subject Performance',
          data: {
            labels: Object.keys(report.stats.subjectAverages),
            datasets: [{
              label: 'Average Performance (%)',
              data: Object.values(report.stats.subjectAverages),
              backgroundColor: 'rgba(59, 130, 246, 0.7)',
              borderColor: 'rgb(59, 130, 246)',
              borderWidth: 2
            }]
          }
        });
      }

      // Topic performance radar
      if (report.stats.topicPerformance) {
        charts.push({
          type: 'radar',
          title: 'Topic Performance',
          data: {
            labels: Object.keys(report.stats.topicPerformance),
            datasets: [{
              label: 'Performance (%)',
              data: Object.values(report.stats.topicPerformance),
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              borderColor: 'rgb(59, 130, 246)',
              pointBackgroundColor: 'rgb(59, 130, 246)',
            }]
          }
        });
      }

      // Progress trend line
      if (report.stats.progressTrend) {
        charts.push({
          type: 'line',
          title: 'Progress Trend',
          data: {
            labels: report.stats.progressTrend.map((_, index) => `Assessment ${index + 1}`),
            datasets: [{
              label: 'Performance (%)',
              data: report.stats.progressTrend,
              borderColor: 'rgb(16, 185, 129)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.4
            }]
          }
        });
      }
    }

    // Task comparison chart
    if (report.type === 'task-comparison' && report.comparisonData) {
      charts.push({
        type: 'bar',
        title: 'Task Comparison',
        data: {
          labels: [report.comparisonData.task1.title, report.comparisonData.task2.title],
          datasets: [{
            label: 'Performance (%)',
            data: [
              report.comparisonData.task1.mark || report.comparisonData.task1.averageMark,
              report.comparisonData.task2.mark || report.comparisonData.task2.averageMark
            ],
            backgroundColor: [
              'rgba(59, 130, 246, 0.7)',
              report.comparisonData.improvement >= 0 ? 'rgba(16, 185, 129, 0.7)' : 'rgba(239, 68, 68, 0.7)'
            ]
          }]
        }
      });
    }

    return charts;
  }

  // Calculate Grade Statistics
  static calculateGradeStatistics(tasks, submissions, marks) {
    const uniqueLearners = new Set(marks.map(m => `${m.learnerSurname}-${m.learnerName}`));
    const percentages = marks.map(m => m.percentageMark);
    const fiveNumberSummary = StatisticsCalculator.calculateFiveNumberSummary(percentages);
    
    // Subject breakdown
    const subjectStats = {};
    tasks.forEach(task => {
      const subjectMarks = marks.filter(m => {
        const taskObj = tasks.find(t => t.id === m.taskId);
        return taskObj?.subject === task.subject;
      });
      
      if (subjectMarks.length > 0) {
        const subjectPercentages = subjectMarks.map(m => m.percentageMark);
        subjectStats[task.subject] = {
          averagePerformance: subjectPercentages.reduce((sum, p) => sum + p, 0) / subjectPercentages.length,
          learnerCount: new Set(subjectMarks.map(m => `${m.learnerSurname}-${m.learnerName}`)).size,
          assessmentCount: subjectMarks.length,
          passRate: (subjectMarks.filter(m => m.percentageMark >= 50).length / subjectMarks.length) * 100
        };
      }
    });

    // Topic analysis
    const topicPerformance = {};
    tasks.forEach(task => {
      if (task.topics) {
        task.topics.forEach(topic => {
          const topicMarks = marks.filter(m => m.taskId === task.id);
          if (topicMarks.length > 0) {
            const avg = topicMarks.reduce((sum, m) => sum + m.percentageMark, 0) / topicMarks.length;
            if (!topicPerformance[topic]) {
              topicPerformance[topic] = [];
            }
            topicPerformance[topic].push(avg);
          }
        });
      }
    });

    // Average topic performance
    const avgTopicPerformance = {};
    Object.keys(topicPerformance).forEach(topic => {
      avgTopicPerformance[topic] = topicPerformance[topic].reduce((sum, avg) => sum + avg, 0) / topicPerformance[topic].length;
    });

    return {
      totalLearners: uniqueLearners.size,
      totalAssessments: marks.length,
      totalTasks: tasks.length,
      totalSubmissions: submissions.length,
      fiveNumberSummary,
      passRate: (marks.filter(m => m.percentageMark >= 50).length / marks.length) * 100,
      performanceLevels: StatisticsCalculator.calculatePerformanceLevels(marks),
      subjectStats,
      topicPerformance: avgTopicPerformance,
      submissionRate: submissions.length > 0 ? 
        (submissions.reduce((sum, s) => sum + s.actualSubmissions, 0) / 
         submissions.reduce((sum, s) => sum + s.expectedSubmissions, 0)) * 100 : 0
    };
  }

  // Calculate Subject Statistics
  static calculateSubjectStatistics(tasks, submissions, marks) {
    const percentages = marks.map(m => m.percentageMark);
    const fiveNumberSummary = StatisticsCalculator.calculateFiveNumberSummary(percentages);
    
    // Task type analysis
    const taskTypeStats = {};
    tasks.forEach(task => {
      const taskMarks = marks.filter(m => m.taskId === task.id);
      if (taskMarks.length > 0) {
        const avg = taskMarks.reduce((sum, m) => sum + m.percentageMark, 0) / taskMarks.length;
        if (!taskTypeStats[task.type]) {
          taskTypeStats[task.type] = [];
        }
        taskTypeStats[task.type].push(avg);
      }
    });

    // Average by task type
    const avgTaskTypePerformance = {};
    Object.keys(taskTypeStats).forEach(type => {
      avgTaskTypePerformance[type] = taskTypeStats[type].reduce((sum, avg) => sum + avg, 0) / taskTypeStats[type].length;
    });

    // Topic analysis
    const topicPerformance = {};
    tasks.forEach(task => {
      if (task.topics) {
        task.topics.forEach(topic => {
          const topicMarks = marks.filter(m => m.taskId === task.id);
          if (topicMarks.length > 0) {
            const avg = topicMarks.reduce((sum, m) => sum + m.percentageMark, 0) / topicMarks.length;
            if (!topicPerformance[topic]) {
              topicPerformance[topic] = [];
            }
            topicPerformance[topic].push(avg);
          }
        });
      }
    });

    const avgTopicPerformance = {};
    Object.keys(topicPerformance).forEach(topic => {
      avgTopicPerformance[topic] = topicPerformance[topic].reduce((sum, avg) => sum + avg, 0) / topicPerformance[topic].length;
    });

    return {
      totalLearners: new Set(marks.map(m => `${m.learnerSurname}-${m.learnerName}`)).size,
      totalAssessments: marks.length,
      totalTasks: tasks.length,
      fiveNumberSummary,
      passRate: (marks.filter(m => m.percentageMark >= 50).length / marks.length) * 100,
      performanceLevels: StatisticsCalculator.calculatePerformanceLevels(marks),
      taskTypePerformance: avgTaskTypePerformance,
      topicPerformance: avgTopicPerformance,
      submissionRate: submissions.length > 0 ? 
        (submissions.reduce((sum, s) => sum + s.actualSubmissions, 0) / 
         submissions.reduce((sum, s) => sum + s.expectedSubmissions, 0)) * 100 : 0
    };
  }

  // Helper method to calculate improvement distribution
  static calculateImprovementDistribution(commonLearners) {
    const distribution = {
      significantImprovement: 0,    // >= 20%
      goodImprovement: 0,           // 10-19%
      moderateImprovement: 0,       // 5-9%
      slightImprovement: 0,         // 0-4%
      slightDecline: 0,             // 0 to -4%
      moderateDecline: 0,           // -5 to -9%
      significantDecline: 0         // <= -10%
    };

    const totalLearners = commonLearners.length;
    
    commonLearners.forEach(learner => {
      const improvement = learner.improvement;
      
      if (improvement >= 20) distribution.significantImprovement++;
      else if (improvement >= 10) distribution.goodImprovement++;
      else if (improvement >= 5) distribution.moderateImprovement++;
      else if (improvement >= 0) distribution.slightImprovement++;
      else if (improvement >= -5) distribution.slightDecline++;
      else if (improvement >= -10) distribution.moderateDecline++;
      else distribution.significantDecline++;
    });

    // Convert to percentages
    return {
      counts: distribution,
      percentages: {
        significantImprovement: totalLearners > 0 ? (distribution.significantImprovement / totalLearners) * 100 : 0,
        goodImprovement: totalLearners > 0 ? (distribution.goodImprovement / totalLearners) * 100 : 0,
        moderateImprovement: totalLearners > 0 ? (distribution.moderateImprovement / totalLearners) * 100 : 0,
        slightImprovement: totalLearners > 0 ? (distribution.slightImprovement / totalLearners) * 100 : 0,
        slightDecline: totalLearners > 0 ? (distribution.slightDecline / totalLearners) * 100 : 0,
        moderateDecline: totalLearners > 0 ? (distribution.moderateDecline / totalLearners) * 100 : 0,
        significantDecline: totalLearners > 0 ? (distribution.significantDecline / totalLearners) * 100 : 0
      },
      totalLearners
    };
  }

  // Helper method to calculate comparison statistics
  static calculateComparisonStatistics(commonLearners) {
    if (commonLearners.length === 0) {
      return {
        improvementStats: {
          mean: 0,
          median: 0,
          standardDeviation: 0,
          minimum: 0,
          maximum: 0,
          q1: 0,
          q3: 0
        },
        task1Stats: {
          mean: 0,
          median: 0,
          standardDeviation: 0,
          minimum: 0,
          maximum: 0
        },
        task2Stats: {
          mean: 0,
          median: 0,
          standardDeviation: 0,
          minimum: 0,
          maximum: 0
        }
      };
    }

    // Extract improvement values
    const improvements = commonLearners.map(learner => learner.improvement);
    const task1Marks = commonLearners.map(learner => learner.task1Mark);
    const task2Marks = commonLearners.map(learner => learner.task2Mark);

    // Calculate statistics for improvements
    const improvementStats = this.calculateBasicStatistics(improvements);
    const task1Stats = this.calculateBasicStatistics(task1Marks);
    const task2Stats = this.calculateBasicStatistics(task2Marks);

    return {
      improvementStats,
      task1Stats,
      task2Stats,
      correlationAnalysis: this.calculateCorrelationInsights(commonLearners)
    };
  }

  // Helper method to calculate basic statistics for an array
  static calculateBasicStatistics(values) {
    if (values.length === 0) {
      return {
        mean: 0,
        median: 0,
        standardDeviation: 0,
        minimum: 0,
        maximum: 0,
        q1: 0,
        q3: 0
      };
    }

    const sorted = [...values].sort((a, b) => a - b);
    const n = values.length;
    
    // Mean
    const mean = values.reduce((sum, val) => sum + val, 0) / n;
    
    // Median
    const median = n % 2 === 0 
      ? (sorted[Math.floor(n / 2) - 1] + sorted[Math.floor(n / 2)]) / 2
      : sorted[Math.floor(n / 2)];
    
    // Standard deviation
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / n;
    const standardDeviation = Math.sqrt(variance);
    
    // Quartiles
    const q1Index = Math.floor(n * 0.25);
    const q3Index = Math.floor(n * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    
    return {
      mean,
      median,
      standardDeviation,
      minimum: sorted[0],
      maximum: sorted[n - 1],
      q1,
      q3
    };
  }

  // Helper method to calculate correlation insights
  static calculateCorrelationInsights(commonLearners) {
    if (commonLearners.length < 2) {
      return {
        strongPerformersImproving: 0,
        weakPerformersImproving: 0,
        consistentPerformers: 0,
        volatilePerformers: 0
      };
    }

    let strongPerformersImproving = 0;
    let weakPerformersImproving = 0;
    let consistentPerformers = 0;
    let volatilePerformers = 0;

    commonLearners.forEach(learner => {
      const avgPerformance = (learner.task1Mark + learner.task2Mark) / 2;
      const improvement = learner.improvement;
      const performanceVariation = Math.abs(improvement);

      // Strong performers who are improving
      if (avgPerformance >= 70 && improvement > 0) {
        strongPerformersImproving++;
      }
      
      // Weak performers who are improving
      if (avgPerformance < 50 && improvement > 0) {
        weakPerformersImproving++;
      }
      
      // Consistent performers (small variation)
      if (performanceVariation <= 5) {
        consistentPerformers++;
      }
      
      // Volatile performers (large variation)
      if (performanceVariation > 15) {
        volatilePerformers++;
      }
    });

    return {
      strongPerformersImproving,
      weakPerformersImproving,
      consistentPerformers,
      volatilePerformers,
      patterns: {
        strongPerformersImprovingRate: (strongPerformersImproving / commonLearners.length) * 100,
        weakPerformersImprovingRate: (weakPerformersImproving / commonLearners.length) * 100,
        consistencyRate: (consistentPerformers / commonLearners.length) * 100,
        volatilityRate: (volatilePerformers / commonLearners.length) * 100
      }
    };
  }

  // Enhanced subject statistics calculation (if this method is also missing)
  static calculateEnhancedSubjectStatistics(taskStructures, submissions, marks) {
    // If this method doesn't exist, here's a basic implementation
    const basicStats = this.calculateSubjectStatistics(marks, taskStructures.map(ts => ts.task));
    
    // Add any enhanced calculations here
    return {
      ...basicStats,
      taskStructureAnalysis: taskStructures.map(ts => ({
        taskName: ts.task.name,
        topicCoverage: Object.keys(ts.structure.topicBreakdown || {}).length,
        difficultyDistribution: ts.structure.taxonomyBreakdown || {},
        questionTypes: ts.structure.questionTypes || {}
      }))
    };
  }

  // Enhanced subject report prompt (if this method is also missing)
  static buildEnhancedSubjectReportPrompt(grade, subject, stats, taskStructures, marks, schoolMappings, context, userProfile) {
    // If this method doesn't exist, fall back to the existing one
    return this.buildSubjectReportPrompt(grade, subject, stats, taskStructures.map(ts => ts.task), marks, userProfile);
  }

  // Enhanced subject chart data generation (if this method is also missing)
  static generateEnhancedSubjectChartData(stats, taskStructures) {
    return {
      performanceDistribution: stats.performanceLevels || [],
      topicPerformance: stats.topicPerformance || {},
      taskTypeComparison: stats.taskTypePerformance || {},
      schoolComparison: stats.schoolAnalysis || []
    };
  }

  // Calculate Individual Learner Statistics
  static calculateLearnerStatistics(marks, tasks) {
    const percentages = marks.map(m => m.percentageMark);
    const averagePerformance = percentages.reduce((sum, p) => sum + p, 0) / percentages.length;
    
    // Sort marks by date to show progression
    const sortedMarks = [...marks].sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateA - dateB;
    });

    const progression = sortedMarks.length > 1 ? 
      sortedMarks[sortedMarks.length - 1].percentageMark - sortedMarks[0].percentageMark : 0;

    // Task type performance
    const taskTypePerformance = {};
    marks.forEach(mark => {
      const task = tasks.find(t => t.id === mark.taskId);
      if (task) {
        if (!taskTypePerformance[task.type]) {
          taskTypePerformance[task.type] = [];
        }
        taskTypePerformance[task.type].push(mark.percentageMark);
      }
    });

    // Average by task type
    const avgTaskTypePerformance = {};
    Object.keys(taskTypePerformance).forEach(type => {
      avgTaskTypePerformance[type] = taskTypePerformance[type].reduce((sum, p) => sum + p, 0) / taskTypePerformance[type].length;
    });

    return {
      averagePerformance,
      highestMark: Math.max(...percentages),
      lowestMark: Math.min(...percentages),
      progression,
      consistency: StatisticsCalculator.calculateStandardDeviation(percentages, averagePerformance),
      passRate: (marks.filter(m => m.percentageMark >= 50).length / marks.length) * 100,
      taskTypePerformance: avgTaskTypePerformance,
      recentPerformance: sortedMarks.slice(-3).map(m => m.percentageMark),
      totalAssessments: marks.length,
      completionRate: (marks.length / tasks.length) * 100
    };
  }

  // Calculate Learner Overall Statistics
  static calculateLearnerOverallStatistics(marks, tasks) {
    const stats = this.calculateLearnerStatistics(marks, tasks);
    
    // Subject breakdown
    const subjectPerformance = {};
    marks.forEach(mark => {
      const task = tasks.find(t => t.id === mark.taskId);
      if (task) {
        if (!subjectPerformance[task.subject]) {
          subjectPerformance[task.subject] = [];
        }
        subjectPerformance[task.subject].push(mark.percentageMark);
      }
    });

    const avgSubjectPerformance = {};
    Object.keys(subjectPerformance).forEach(subject => {
      avgSubjectPerformance[subject] = subjectPerformance[subject].reduce((sum, p) => sum + p, 0) / subjectPerformance[subject].length;
    });

    return {
      ...stats,
      overallAverage: stats.averagePerformance,
      subjectPerformance: avgSubjectPerformance,
      strongestSubject: Object.entries(avgSubjectPerformance).reduce((a, b) => a[1] > b[1] ? a : b, ['', 0])[0],
      weakestSubject: Object.entries(avgSubjectPerformance).reduce((a, b) => a[1] < b[1] ? a : b, ['', 100])[0]
    };
  }

  static buildSubjectReportPrompt(grade, subject, stats, tasks, marks, userProfile) {
  return `Generate a comprehensive subject performance report for **Grade ${grade} ${subject}**. Format your response using clear markdown formatting with headers, bullet points, tables where appropriate, and emphasis for key insights.

## EDUCATOR CONTEXT
- **School:** ${userProfile.schoolName || 'Not specified'}
- **Institution:** ${userProfile.institution}
- **Position:** ${userProfile.position}
- **Location:** ${userProfile.district}, ${userProfile.province}
- **Subject Focus:** ${subject}

## SUBJECT ASSESSMENT DATA OVERVIEW
| Metric | Value |
|--------|-------|
| Total Learners | ${stats.totalLearners} |
| Total Tasks | ${stats.totalTasks} |
| Total Assessments Completed | ${stats.totalAssessments} |
| Subject Average | ${stats.fiveNumberSummary.mean.toFixed(1)}% |
| Pass Rate | ${stats.passRate.toFixed(1)}% |
| Submission Rate | ${stats.submissionRate.toFixed(1)}% |

## PERFORMANCE DISTRIBUTION
${stats.performanceLevels.map(level => 
  `- **Level ${level.level} (${level.range}):** ${level.count} learners (${level.percentage.toFixed(1)}%)`
).join('\n')}

## TASK TYPE ANALYSIS
${Object.entries(stats.taskTypePerformance).map(([type, avg]) => 
  `### ${type}
- **Average Performance:** ${avg.toFixed(1)}%
- **Task Count:** ${tasks.filter(t => t.type === type).length}`
).join('\n\n')}

## TOPIC PERFORMANCE BREAKDOWN
${Object.entries(stats.topicPerformance)
  .sort((a, b) => b[1] - a[1])
  .map(([topic, avg], index) => `${index + 1}. **${topic}:** ${avg.toFixed(1)}%`)
  .join('\n')}

## STATISTICAL SUMMARY
- **Highest Performance:** ${stats.fiveNumberSummary.maximum.toFixed(1)}%
- **Lowest Performance:** ${stats.fiveNumberSummary.minimum.toFixed(1)}%
- **Median:** ${stats.fiveNumberSummary.median.toFixed(1)}%
- **Q1 (25th percentile):** ${stats.fiveNumberSummary.q1.toFixed(1)}%
- **Q3 (75th percentile):** ${stats.fiveNumberSummary.q3.toFixed(1)}%
- **Standard Deviation:** ${stats.fiveNumberSummary.standardDeviation.toFixed(1)}

## ASSESSMENT TASKS OVERVIEW
${tasks.map(task => {
  const taskMarks = marks.filter(m => m.taskId === task.id);
  const taskAvg = taskMarks.length > 0 
    ? (taskMarks.reduce((sum, m) => sum + m.percentageMark, 0) / taskMarks.length).toFixed(1)
    : 'N/A';
  return `### ${task.name}
- **Type:** ${task.type}
- **Total Marks:** ${task.totalMarks}
- **Learners Assessed:** ${taskMarks.length}
- **Average Performance:** ${taskAvg}%
- **Topics:** ${(task.topics || []).join(', ') || 'Not specified'}`;
}).join('\n\n')}

Please provide a comprehensive subject-specific analysis using markdown formatting including:

# 1. 📊 Subject Performance Overview
Provide key insights about overall ${subject} performance in Grade ${grade}.

# 2. 📈 Performance Trends and Patterns
Analyze trends across different assessment types and time periods.

# 3. 🎯 Topic-wise Analysis
Detailed breakdown of performance across different ${subject} topics.

# 4. 📋 Assessment Type Effectiveness
Analysis of how different assessment types (tests, assignments, projects) reveal student understanding.

# 5. ✅ Subject Strengths and Achievements
What students are excelling at in ${subject}.

# 6. 🔧 Areas Requiring Immediate Attention
Specific ${subject} concepts or skills that need focused intervention.

# 7. 🎓 Curriculum Alignment Assessment
How well student performance aligns with ${subject} curriculum expectations.

# 8. 🎯 Subject-Specific Teaching Strategies
Targeted recommendations for improving ${subject} instruction.

# 9. 📚 Resource and Material Recommendations
Suggested resources to enhance ${subject} learning.

# 10. 📝 Assessment Strategy Improvements
How to optimize future ${subject} assessments for better learning outcomes.

# 11. 👥 Differentiation Strategies
Approaches to meet diverse learning needs in ${subject}.

# 12. 🎯 Next Steps and Action Plan
Specific, actionable steps to improve ${subject} performance.

Format each section with clear headers, use bullet points for lists, **bold** for emphasis, and include relevant emojis to make the report engaging and easy to read. Focus on actionable insights specific to ${subject} teaching and learning.`;
}

  // Build prompts for Gemini API
  static buildGradeReportPrompt(grade, stats, tasks, marks, userProfile) {
  return `Generate a comprehensive educational performance report for Grade ${grade} students. Format your response using clear markdown formatting with headers, bullet points, tables where appropriate, and emphasis for key insights.

## EDUCATOR CONTEXT
- **School:** ${userProfile.schoolName || 'Not specified'}
- **Institution:** ${userProfile.institution}
- **Position:** ${userProfile.position}
- **Location:** ${userProfile.district}, ${userProfile.province}

## ASSESSMENT DATA OVERVIEW
| Metric | Value |
|--------|-------|
| Total Learners | ${stats.totalLearners} |
| Total Tasks | ${stats.totalTasks} |
| Total Assessments Completed | ${stats.totalAssessments} |
| Class Average | ${stats.fiveNumberSummary.mean.toFixed(1)}% |
| Pass Rate | ${stats.passRate.toFixed(1)}% |
| Submission Rate | ${stats.submissionRate.toFixed(1)}% |

## PERFORMANCE DISTRIBUTION
${stats.performanceLevels.map(level => 
  `- **Level ${level.level} (${level.range}):** ${level.count} learners (${level.percentage.toFixed(1)}%)`
).join('\n')}

## SUBJECT BREAKDOWN
${Object.entries(stats.subjectStats).map(([subject, data]) => 
  `### ${subject}
- **Average Performance:** ${data.averagePerformance.toFixed(1)}%
- **Learners Assessed:** ${data.learnerCount}
- **Pass Rate:** ${data.passRate.toFixed(1)}%`
).join('\n\n')}

## TOP PERFORMING TOPICS
${Object.entries(stats.topicPerformance)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 5)
  .map(([topic, avg], index) => `${index + 1}. **${topic}:** ${avg.toFixed(1)}%`)
  .join('\n')}

## AREAS NEEDING ATTENTION
${Object.entries(stats.topicPerformance)
  .sort((a, b) => a[1] - b[1])
  .slice(0, 5)
  .map(([topic, avg], index) => `${index + 1}. **${topic}:** ${avg.toFixed(1)}% ⚠️`)
  .join('\n')}

Please provide a comprehensive analysis using markdown formatting including:

# 1. 📊 Overall Grade Performance Summary
Provide key insights about the grade's overall academic performance.

# 2. 📚 Subject-wise Analysis and Trends
Detailed breakdown of performance across different subjects.

# 3. 🔍 Identification of Learning Gaps
Specific areas where students are struggling.

# 4. ✅ Strengths and Areas for Improvement
What's working well and what needs attention.

# 5. 🎯 Teaching Strategy Recommendations
Specific, actionable recommendations for educators.

# 6. 🆘 Intervention Program Suggestions
Programs to support struggling learners.

# 7. 📖 Curriculum Focus Areas
Areas that need more emphasis in teaching.

# 8. 📝 Assessment Strategy Recommendations
How to improve future assessments.

Format each section with clear headers, use bullet points for lists, **bold** for emphasis, and include relevant emojis to make the report engaging and easy to read.`;
}

  static buildLearnerSubjectReportPrompt(surname, name, grade, subject, stats, marks, tasks, userProfile) {
  return `Generate an individual learner performance report for **${surname}, ${name}** in **Grade ${grade} ${subject}**. Use markdown formatting for clear presentation.

## 👤 LEARNER PROFILE
- **Name:** ${surname}, ${name}
- **Grade:** ${grade}
- **Subject:** ${subject}

## 📊 PERFORMANCE DATA
| Metric | Value |
|--------|-------|
| Overall Subject Average | ${stats.averagePerformance.toFixed(1)}% |
| Highest Achievement | ${stats.highestMark.toFixed(1)}% |
| Lowest Performance | ${stats.lowestMark.toFixed(1)}% |
| Performance Progression | ${stats.progression > 0 ? '+' : ''}${stats.progression.toFixed(1)}% |
| Consistency Score | ${(100 - stats.consistency).toFixed(1)}% |
| Pass Rate | ${stats.passRate.toFixed(1)}% |
| Completion Rate | ${stats.completionRate.toFixed(1)}% |

## 📋 ASSESSMENT TYPE BREAKDOWN
${Object.entries(stats.taskTypePerformance).map(([type, avg]) => 
  `- **${type}:** ${avg.toFixed(1)}% average`
).join('\n')}

## 📈 RECENT PERFORMANCE TREND
${stats.recentPerformance.map((perf, index) => 
  `- **Assessment ${index + 1}:** ${perf.toFixed(1)}%`
).join('\n')}

Please provide a comprehensive individual learner analysis with markdown formatting including:

# 1. 📊 Overall Performance Summary
# 2. 📈 Learning Progress Assessment
# 3. ✅ Strengths and Achievements
# 4. 🔧 Areas Requiring Support
# 5. 🎨 Learning Style Observations
# 6. 🎯 Specific Intervention Recommendations
# 7. 👨‍👩‍👧‍👦 Parent/Guardian Communication Points
# 8. 📋 Individual Learning Plan Suggestions
# 9. 🎯 Goal Setting for Future Assessments

Focus on constructive, actionable feedback suitable for learner development planning and parent consultations. Use clear headers, bullet points, and emphasis for key insights.`;
}

  static buildLearnerOverallReportPrompt(surname, name, grade, stats, marks, tasks, userProfile) {
  return `Generate a comprehensive overall academic performance report for **${surname}, ${name}** in **Grade ${grade}**. Use markdown formatting for professional presentation.

## 👤 LEARNER PROFILE
- **Name:** ${surname}, ${name}
- **Grade:** ${grade}
- **Academic Year:** ${new Date().getFullYear()}

## 📊 OVERALL ACADEMIC PERFORMANCE
| Metric | Value |
|--------|-------|
| Overall Average | ${stats.overallAverage.toFixed(1)}% |
| Highest Achievement | ${stats.highestMark.toFixed(1)}% |
| Lowest Performance | ${stats.lowestMark.toFixed(1)}% |
| Academic Progression | ${stats.progression > 0 ? '+' : ''}${stats.progression.toFixed(1)}% |
| Performance Consistency | ${(100 - stats.consistency).toFixed(1)}% |
| Overall Pass Rate | ${stats.passRate.toFixed(1)}% |

## 📚 SUBJECT PERFORMANCE BREAKDOWN
${Object.entries(stats.subjectPerformance).map(([subject, avg]) => 
  `- **${subject}:** ${avg.toFixed(1)}% average`
).join('\n')}

## 🏆 ACADEMIC STRENGTHS
- **Strongest Subject:** ${stats.strongestSubject} (${stats.subjectPerformance[stats.strongestSubject]?.toFixed(1)}%)

## 🔧 AREAS FOR DEVELOPMENT  
- **Subject Needing Support:** ${stats.weakestSubject} (${stats.subjectPerformance[stats.weakestSubject]?.toFixed(1)}%)

## 📋 ASSESSMENT TYPE PERFORMANCE
${Object.entries(stats.taskTypePerformance).map(([type, avg]) => 
  `- **${type}:** ${avg.toFixed(1)}% average`
).join('\n')}

Please provide a holistic academic performance analysis with markdown formatting including:

# 1. 🎓 Overall Academic Achievement Summary
# 2. 🔄 Cross-Curricular Performance Analysis
# 3. 💪 Learning Strengths and Talents
# 4. 🆘 Academic Challenges and Support Needs
# 5. 🎯 Learning Behavior and Engagement Patterns
# 6. 📚 Recommendations for Academic Support
# 7. 🌟 Holistic Development Suggestions
# 8. 👨‍👩‍👧‍👦 Parent Engagement Strategies
# 9. 📅 Long-term Academic Planning
# 10. 🚀 Career Guidance Considerations

Provide insights suitable for comprehensive academic planning, parent consultations, and learner development discussions. Use clear markdown formatting with headers, bullet points, tables, and emphasis for key insights.`;
}

static buildEnhancedGradeReportPrompt(grade, stats, marks, tasks, learnerBreakdown, userProfile) {
  return `Generate a comprehensive Grade ${grade} performance report with enhanced learner and school analysis. Use markdown formatting for professional presentation.

## 🏫 SCHOOL PARTICIPATION OVERVIEW
**Total Schools Represented:** ${learnerBreakdown.schoolCount}
**Total Learners:** ${learnerBreakdown.totalLearners}

### School Performance Rankings:
${learnerBreakdown.schools.map((school, index) => 
  `${index + 1}. **${school.schoolName}** - ${school.learnerCount} learners (${school.averagePerformance.toFixed(1)}% avg)`
).join('\n')}

## 👥 LEARNER COMPOSITION BY SCHOOL
${learnerBreakdown.schools.map(school => `
### ${school.schoolName}
- **Learners:** ${school.learnerCount}
- **Average Performance:** ${school.averagePerformance.toFixed(1)}%
- **Top Performers:** ${school.learnerList.slice(0, 3).map(l => `${l.surname}, ${l.name} (${l.averagePerformance.toFixed(1)}%)`).join('; ')}
`).join('\n')}

## 📊 OVERALL GRADE STATISTICS
[Include existing statistics here...]

Please provide comprehensive analysis with markdown formatting including:

# 1. 🏫 School-by-School Performance Analysis
# 2. 👥 Individual Learner Highlights (by school)  
# 3. 📊 Cross-School Performance Comparison
# 4. 🎯 School-Specific Recommendations
# 5. 👨‍🏫 Collaborative Teaching Opportunities
# 6. 📈 Inter-School Best Practices
# 7. 🏆 Recognition and Celebrations
# 8. 📋 School-Specific Action Plans

Focus on celebrating diversity, recognizing all schools, and providing actionable insights for both individual schools and cross-school collaboration.`;
}

  static async generateTaskComparisonReport(task1Id, task2Id, scope, selectedLearner, userProfile, context = '') {
    try {
      // Get complete task details
      const [task1, task2] = await Promise.all([
        AssessmentService.getAssessmentTask(task1Id),
        AssessmentService.getAssessmentTask(task2Id)
      ]);

      // Extract detailed task structures (preserving existing functionality)
      const task1Structure = TaskAnalysisService.finalizeBreakdowns(
        TaskAnalysisService.extractTaskStructure(task1)
      );
      const task2Structure = TaskAnalysisService.finalizeBreakdowns(
        TaskAnalysisService.extractTaskStructure(task2)
      );

      // Compare task structures (preserving existing functionality)
      const structureComparison = TaskAnalysisService.compareTaskStructures(task1Structure, task2Structure);

      // Get marks for both tasks
      const [marks1, marks2] = await Promise.all([
        AssessmentService.getLearnerMarks(task1Id),
        AssessmentService.getLearnerMarks(task2Id)
      ]);

      // Build school mappings for enhanced analysis
      const schoolMappings = new Map();
      [...marks1, ...marks2].forEach(mark => {
        const learnerKey = `${mark.learnerSurname}|${mark.learnerName}`;
        if (mark.school && !schoolMappings.has(learnerKey)) {
          schoolMappings.set(learnerKey, mark.school);
        }
      });

      let comparisonData;
      let topicPerformanceAnalysis = {};

      if (scope === 'learner' && selectedLearner) {
        const [surname, name] = selectedLearner.split('|');
        
        // Enhanced individual learner comparison (preserving existing + adding school info)
        comparisonData = this.calculateEnhancedLearnerTaskComparison(
          task1, task2, marks1, marks2, surname, name, task1Structure, task2Structure, schoolMappings
        );

        // Get detailed topic performance for the learner (preserving existing)
        const learnerMark1 = marks1.find(m => m.learnerSurname === surname && m.learnerName === name);
        const learnerMark2 = marks2.find(m => m.learnerSurname === surname && m.learnerName === name);

        if (learnerMark1 && learnerMark2) {
          const topicPerf1 = TaskAnalysisService.analyzeLearnerPerformanceByTopic(learnerMark1, task1Structure);
          const topicPerf2 = TaskAnalysisService.analyzeLearnerPerformanceByTopic(learnerMark2, task2Structure);
          
          topicPerformanceAnalysis = {
            task1TopicPerformance: topicPerf1,
            task2TopicPerformance: topicPerf2
          };
        }
      } else {
        // Enhanced grade comparison (preserving existing + adding school/learner analysis)
        comparisonData = this.calculateEnhancedGradeTaskComparison(
          task1, task2, marks1, marks2, task1Structure, task2Structure, schoolMappings
        );

        // Calculate average topic performance for the grade (preserving existing)
        topicPerformanceAnalysis = this.calculateGradeTopicPerformance(
          marks1, marks2, task1Structure, task2Structure
        );
      }

      const prompt = this.buildEnhancedTaskComparisonPrompt(
        task1, task2, task1Structure, task2Structure, structureComparison, 
        comparisonData, topicPerformanceAnalysis, scope, selectedLearner, context, userProfile
      );
      
      const aiAnalysis = await generateResponse(prompt);

      return {
        type: 'task-comparison',
        scope,
        task1: { ...task1, structure: task1Structure },
        task2: { ...task2, structure: task2Structure },
        structureComparison,
        comparisonData,
        topicPerformanceAnalysis,
        generatedAt: new Date(),
        stats: comparisonData,
        aiAnalysis,
        context,
        rawData: {
          task1Id,
          task2Id,
          comparisonScope: scope,
          totalLearners: comparisonData.totalLearners || 1,
          schoolCount: comparisonData.totalSchools || (comparisonData.school ? 1 : 0)
        }
      };
    } catch (error) {
      console.error('Error generating enhanced task comparison report:', error);
      throw error;
    }
  }

  static calculateLearnerTaskComparison(task1, task2, marks1, marks2, surname, name) {
    const learnerMark1 = marks1.find(m => m.learnerSurname === surname && m.learnerName === name);
    const learnerMark2 = marks2.find(m => m.learnerSurname === surname && m.learnerName === name);

    if (!learnerMark1 || !learnerMark2) {
      throw new Error('Learner marks not found for one or both tasks');
    }

    const improvement = learnerMark2.percentageMark - learnerMark1.percentageMark;
    
    return {
      learner: `${surname}, ${name}`,
      task1: {
        title: task1.title,
        mark: learnerMark1.percentageMark,
        totalMarks: learnerMark1.totalMarks,
        achievedMarks: learnerMark1.achievedMarks,
        date: task1.createdAt
      },
      task2: {
        title: task2.title,
        mark: learnerMark2.percentageMark,
        totalMarks: learnerMark2.totalMarks,
        achievedMarks: learnerMark2.achievedMarks,
        date: task2.createdAt
      },
      improvement,
      improvementPercentage: learnerMark1.percentageMark > 0 ? (improvement / learnerMark1.percentageMark) * 100 : 0,
      topics1: task1.topics || [],
      topics2: task2.topics || []
    };
  }

  static calculateEnhancedLearnerTaskComparison(task1, task2, marks1, marks2, surname, name, task1Structure, task2Structure, schoolMappings) {
    const learnerMark1 = marks1.find(m => m.learnerSurname === surname && m.learnerName === name);
    const learnerMark2 = marks2.find(m => m.learnerSurname === surname && m.learnerName === name);

    if (!learnerMark1 || !learnerMark2) {
      throw new Error('Learner marks not found for one or both tasks');
    }

    const improvement = (learnerMark2.percentageMark || 0) - (learnerMark1.percentageMark || 0);
    const learnerKey = `${surname}|${name}`;
    const school = schoolMappings.get(learnerKey) || learnerMark1.school || learnerMark2.school || 'School Not Specified';
    
    // Get topic-level performance (with safe defaults)
    const topicPerf1 = TaskAnalysisService.analyzeLearnerPerformanceByTopic?.(learnerMark1, task1Structure) || { topicPerformance: {} };
    const topicPerf2 = TaskAnalysisService.analyzeLearnerPerformanceByTopic?.(learnerMark2, task2Structure) || { topicPerformance: {} };

    // Calculate topic improvements (with safe defaults)
    const topicImprovements = this.calculateTopicImprovements(topicPerf1, topicPerf2);

    return {
      type: 'individual-comparison',
      learner: `${surname}, ${name}`,
      school: school,
      improvement: improvement || 0,
      improvementPercentage: (learnerMark1.percentageMark && learnerMark1.percentageMark > 0) ? 
        (improvement / learnerMark1.percentageMark) * 100 : 0,
      topicImprovements: topicImprovements || {},
      task1: {
        title: task1?.name || 'Unknown Task',
        mark: learnerMark1.percentageMark || 0,
        totalMarks: learnerMark1.totalMarks || 0,
        achievedMarks: learnerMark1.achievedMarks || 0,
        date: task1?.createdAt || new Date(),
        structure: task1Structure || {},
        topicPerformance: topicPerf1
      },
      task2: {
        title: task2?.name || 'Unknown Task',
        mark: learnerMark2.percentageMark || 0,
        totalMarks: learnerMark2.totalMarks || 0,
        achievedMarks: learnerMark2.achievedMarks || 0,
        date: task2?.createdAt || new Date(),
        structure: task2Structure || {},
        topicPerformance: topicPerf2
      },
      performanceClassification: {
        task1Level: this.getPerformanceLevel(learnerMark1.percentageMark || 0),
        task2Level: this.getPerformanceLevel(learnerMark2.percentageMark || 0),
        improvementCategory: this.getImprovementCategory(improvement || 0)
      }
    };
  }

  static calculateTopicImprovements(topicPerf1, topicPerf2) {
    const improvements = {};
    
    // Get all topics from both tasks
    const allTopics = new Set([
      ...Object.keys(topicPerf1.topicPerformance || {}),
      ...Object.keys(topicPerf2.topicPerformance || {})
    ]);

    allTopics.forEach(topic => {
      const perf1 = topicPerf1.topicPerformance[topic]?.percentage || 0;
      const perf2 = topicPerf2.topicPerformance[topic]?.percentage || 0;
      
      improvements[topic] = {
        task1Performance: perf1,
        task2Performance: perf2,
        improvement: perf2 - perf1,
        improvementPercentage: perf1 > 0 ? ((perf2 - perf1) / perf1) * 100 : 0
      };
    });

    return improvements;
  }

  static calculateGradeTopicPerformance(marks1, marks2, task1Structure, task2Structure) {
    // Calculate average performance by topic for the entire grade
    const gradeTopicPerf1 = {};
    const gradeTopicPerf2 = {};

    // Process all learner marks for task 1
    marks1.forEach(mark => {
      const topicPerf = TaskAnalysisService.analyzeLearnerPerformanceByTopic(mark, task1Structure);
      Object.keys(topicPerf.topicPerformance || {}).forEach(topic => {
        if (!gradeTopicPerf1[topic]) {
          gradeTopicPerf1[topic] = { total: 0, count: 0 };
        }
        gradeTopicPerf1[topic].total += topicPerf.topicPerformance[topic].percentage;
        gradeTopicPerf1[topic].count += 1;
      });
    });

    // Process all learner marks for task 2
    marks2.forEach(mark => {
      const topicPerf = TaskAnalysisService.analyzeLearnerPerformanceByTopic(mark, task2Structure);
      Object.keys(topicPerf.topicPerformance || {}).forEach(topic => {
        if (!gradeTopicPerf2[topic]) {
          gradeTopicPerf2[topic] = { total: 0, count: 0 };
        }
        gradeTopicPerf2[topic].total += topicPerf.topicPerformance[topic].percentage;
        gradeTopicPerf2[topic].count += 1;
      });
    });

    // Calculate averages
    const avgTopicPerf1 = {};
    const avgTopicPerf2 = {};

    Object.keys(gradeTopicPerf1).forEach(topic => {
      avgTopicPerf1[topic] = gradeTopicPerf1[topic].total / gradeTopicPerf1[topic].count;
    });

    Object.keys(gradeTopicPerf2).forEach(topic => {
      avgTopicPerf2[topic] = gradeTopicPerf2[topic].total / gradeTopicPerf2[topic].count;
    });

    return {
      task1GradeTopicPerformance: avgTopicPerf1,
      task2GradeTopicPerformance: avgTopicPerf2
    };
  }

  static calculateSchoolTopicPerformance(schoolLearners, marks1, marks2, task1Structure, task2Structure) {
    const schoolTopicPerf = {
      task1Topics: new Map(),
      task2Topics: new Map(),
      topicImprovements: new Map()
    };

    schoolLearners.forEach(learner => {
      const [surname, name] = learner.key.split('|');
      
      // Find marks for this learner
      const mark1 = marks1.find(m => m.learnerSurname === surname && m.learnerName === name);
      const mark2 = marks2.find(m => m.learnerSurname === surname && m.learnerName === name);
      
      if (mark1 && mark2) {
        // Get topic performance for this learner
        const topicPerf1 = TaskAnalysisService.analyzeLearnerPerformanceByTopic(mark1, task1Structure);
        const topicPerf2 = TaskAnalysisService.analyzeLearnerPerformanceByTopic(mark2, task2Structure);
        
        // Aggregate topic performance for school
        Object.keys(topicPerf1.topicPerformance || {}).forEach(topic => {
          if (!schoolTopicPerf.task1Topics.has(topic)) {
            schoolTopicPerf.task1Topics.set(topic, { total: 0, count: 0 });
          }
          const topicData = schoolTopicPerf.task1Topics.get(topic);
          topicData.total += topicPerf1.topicPerformance[topic].percentage;
          topicData.count += 1;
        });
        
        Object.keys(topicPerf2.topicPerformance || {}).forEach(topic => {
          if (!schoolTopicPerf.task2Topics.has(topic)) {
            schoolTopicPerf.task2Topics.set(topic, { total: 0, count: 0 });
          }
          const topicData = schoolTopicPerf.task2Topics.get(topic);
          topicData.total += topicPerf2.topicPerformance[topic].percentage;
          topicData.count += 1;
        });
      }
    });

    // Calculate averages and improvements
    const topicAverages1 = {};
    const topicAverages2 = {};
    const topicImprovements = {};

    schoolTopicPerf.task1Topics.forEach((data, topic) => {
      topicAverages1[topic] = data.total / data.count;
    });

    schoolTopicPerf.task2Topics.forEach((data, topic) => {
      topicAverages2[topic] = data.total / data.count;
    });

    // Calculate improvements for common topics
    Object.keys(topicAverages1).forEach(topic => {
      if (topicAverages2[topic] !== undefined) {
        topicImprovements[topic] = topicAverages2[topic] - topicAverages1[topic];
      }
    });

    return {
      task1Averages: topicAverages1,
      task2Averages: topicAverages2,
      improvements: topicImprovements
    };
  }

  static calculateEnhancedGradeTaskComparison(task1, task2, marks1, marks2, task1Structure, task2Structure, schoolMappings) {
    // Basic grade statistics (preserving existing)
    const avg1 = marks1.length > 0 ? marks1.reduce((sum, m) => sum + m.percentageMark, 0) / marks1.length : 0;
    const avg2 = marks2.length > 0 ? marks2.reduce((sum, m) => sum + m.percentageMark, 0) / marks2.length : 0;
    const improvement = avg2 - avg1;

    // Find common learners between both tasks (preserving existing)
    const commonLearners = this.findCommonLearners(marks1, marks2);
    
    // Enhanced: School performance analysis
    const schoolPerformance = new Map();
    
    commonLearners.forEach(learner => {
      const school = schoolMappings.get(learner.key) || 'School Not Specified';
      
      if (!schoolPerformance.has(school)) {
        schoolPerformance.set(school, {
          schoolName: school,
          learners: [],
          task1Performance: [],
          task2Performance: [],
          improvements: [],
          topicImprovements: new Map() // Enhanced: track topic improvements by school
        });
      }
      
      const schoolData = schoolPerformance.get(school);
      schoolData.learners.push(learner);
      schoolData.task1Performance.push(learner.task1Mark);
      schoolData.task2Performance.push(learner.task2Mark);
      schoolData.improvements.push(learner.improvement);
    });

    // Enhanced: Calculate school averages and topic performance
    const schoolAnalysis = Array.from(schoolPerformance.values()).map(school => {
      const avgTask1 = school.task1Performance.reduce((a, b) => a + b, 0) / school.task1Performance.length;
      const avgTask2 = school.task2Performance.reduce((a, b) => a + b, 0) / school.task2Performance.length;
      const avgImprovement = school.improvements.reduce((a, b) => a + b, 0) / school.improvements.length;
      
      // Enhanced: Calculate topic performance by school
      const schoolTopicPerformance = this.calculateSchoolTopicPerformance(
        school.learners, marks1, marks2, task1Structure, task2Structure
      );
      
      return {
        ...school,
        learnerCount: school.learners.length,
        avgTask1Performance: avgTask1,
        avgTask2Performance: avgTask2,
        averageImprovement: avgImprovement,
        improvementPercentage: avgTask1 > 0 ? (avgImprovement / avgTask1) * 100 : 0,
        topicPerformance: schoolTopicPerformance, // Enhanced: topic analysis by school
        performanceLevel: this.getPerformanceLevel(avgTask2),
        improvementCategory: this.getImprovementCategory(avgImprovement)
      };
    });

    // Enhanced: Sort and identify top performers and those needing attention
    const bestPerformingSchools = schoolAnalysis
      .sort((a, b) => b.averageImprovement - a.averageImprovement)
      .slice(0, 3);
      
    const schoolsNeedingAttention = schoolAnalysis
      .sort((a, b) => a.averageImprovement - b.averageImprovement)
      .slice(0, 3);

    const bestPerformingLearners = commonLearners
      .sort((a, b) => b.improvement - a.improvement)
      .slice(0, 5)
      .map(learner => ({
        ...learner,
        school: schoolMappings.get(learner.key) || 'School Not Specified',
        performanceLevel: this.getPerformanceLevel(learner.task2Mark),
        improvementCategory: this.getImprovementCategory(learner.improvement)
      }));
      
    const learnersNeedingAttention = commonLearners
      .sort((a, b) => a.improvement - b.improvement)
      .slice(0, 5)
      .map(learner => ({
        ...learner,
        school: schoolMappings.get(learner.key) || 'School Not Specified',
        performanceLevel: this.getPerformanceLevel(learner.task2Mark),
        improvementCategory: this.getImprovementCategory(learner.improvement)
      }));

    return {
      type: 'grade-comparison',
      totalLearners: commonLearners.length,
      totalSchools: schoolAnalysis.length,
      
      // Preserving existing overall performance
      overallTask1Average: avg1,
      overallTask2Average: avg2,
      overallImprovement: improvement,
      overallImprovementPercentage: avg1 > 0 ? (improvement / avg1) * 100 : 0,
      
      // Preserving existing task details
      task1: {
        title: task1.name,
        averageMark: avg1,
        totalLearners: marks1.length,
        passRate: marks1.filter(m => m.percentageMark >= 50).length / marks1.length * 100,
        date: task1.createdAt,
        structure: task1Structure
      },
      task2: {
        title: task2.name,
        averageMark: avg2,
        totalLearners: marks2.length,
        passRate: marks2.filter(m => m.percentageMark >= 50).length / marks2.length * 100,
        date: task2.createdAt,
        structure: task2Structure
      },
      
      // Enhanced: School and learner analysis
      schoolAnalysis: schoolAnalysis.sort((a, b) => a.schoolName.localeCompare(b.schoolName)),
      bestPerformingSchools,
      schoolsNeedingAttention,
      bestPerformingLearners,
      learnersNeedingAttention,
      
      // Enhanced: Performance distribution
      improvementDistribution: this.calculateImprovementDistribution(commonLearners),
      statisticalSummary: this.calculateComparisonStatistics(commonLearners)
    };
  }

  static buildTaskComparisonPrompt(task1, task2, comparisonData, scope, selectedLearner, context, userProfile) {
    const contextSection = context ? `\n\n**ADDITIONAL CONTEXT:**\n${context}\n` : '';
    
    return `Generate a comprehensive task comparison analysis report using markdown formatting.

## 📊 TASK COMPARISON ANALYSIS

**Comparison Scope:** ${scope === 'learner' ? 'Individual Learner' : 'Grade Level'}
${scope === 'learner' ? `**Learner:** ${comparisonData.learner}` : ''}

## 📋 TASK DETAILS
### Task 1: ${comparisonData.task1.title}
- **Date:** ${new Date(comparisonData.task1.date).toLocaleDateString()}
- **Performance:** ${comparisonData.task1.mark || comparisonData.task1.averageMark}%
${scope === 'grade' ? `- **Learners:** ${comparisonData.task1.totalLearners}` : ''}

### Task 2: ${comparisonData.task2.title}
- **Date:** ${new Date(comparisonData.task2.date).toLocaleDateString()}
- **Performance:** ${comparisonData.task2.mark || comparisonData.task2.averageMark}%
${scope === 'grade' ? `- **Learners:** ${comparisonData.task2.totalLearners}` : ''}

## 📈 PERFORMANCE COMPARISON
- **Change:** ${comparisonData.improvement > 0 ? '+' : ''}${comparisonData.improvement.toFixed(1)}%
- **Improvement Rate:** ${comparisonData.improvementPercentage.toFixed(1)}%

${contextSection}

Please provide a detailed analysis with markdown formatting including:

# 1. 📊 Performance Comparison Summary
# 2. 📈 Trend Analysis
# 3. ✅ Improvement Areas Identified
# 4. 🔍 Areas of Concern
# 5. 📚 Topic/Content Analysis
# 6. 🎯 Actionable Recommendations
# 7. 📋 Next Steps and Interventions

Focus on actionable insights that help improve teaching and learning outcomes.`;
  }

  static buildEnhancedTaskComparisonPrompt(task1, task2, task1Structure, task2Structure, structureComparison, comparisonData, topicPerformanceAnalysis, scope, selectedLearner, context, userProfile) {
    const contextSection = context ? `\n\n**ADDITIONAL CONTEXT:**\n${context}\n` : '';
    
    // Safe helper function to format numbers
    const safeToFixed = (value, decimals = 1) => {
      return (value !== undefined && value !== null && !isNaN(value)) ? Number(value).toFixed(decimals) : '0.0';
    };

    // Safe helper function to get value or default
    const safeValue = (value, defaultValue = 0) => {
      return (value !== undefined && value !== null && !isNaN(value)) ? value : defaultValue;
    };
    
    // Build detailed task structure information (preserving existing)
    const buildTaskStructureInfo = (task, structure) => {
      if (!task || !structure) return 'Task information not available';
      
      return `
  **Task:** ${task.name || 'Unnamed Task'}
  **Type:** ${task.type || 'Not specified'}
  **Total Marks:** ${safeValue(structure.totalMarks)}
  **Question Count:** ${safeValue(structure.questionCount)}
  **Papers:** ${task.papers?.join(', ') || 'Not specified'}

  **Topic Breakdown:**
  ${Object.entries(structure.topicBreakdown || {}).map(([topic, data]) => 
    `- ${topic}: ${safeValue(data.totalMarks)} marks (${safeToFixed(data.percentage)}%) - ${safeValue(data.questionCount)} questions`
  ).join('\n') || 'No topic breakdown available'}

  **Subtopic Breakdown:**
  ${Object.entries(structure.subtopicBreakdown || {}).map(([subtopic, data]) => 
    `- ${subtopic}: ${safeValue(data.totalMarks)} marks (${safeToFixed(data.percentage)}%)`
  ).join('\n') || 'No subtopic breakdown available'}

  **Concepts Covered:**
  ${Object.entries(structure.conceptBreakdown || {}).map(([concept, data]) => 
    `- ${concept}: ${safeValue(data.totalMarks)} marks (${safeToFixed(data.percentage)}%)`
  ).join('\n') || 'No concept breakdown available'}

  **Skills Assessed:**
  ${Object.entries(structure.skillsBreakdown || {}).map(([skill, data]) => 
    `- ${skill}: ${safeValue(data.totalMarks)} marks (${safeToFixed(data.percentage)}%)`
  ).join('\n') || 'No skills breakdown available'}

  **Taxonomy/Difficulty Levels:**
  ${Object.entries(structure.taxonomyBreakdown || {}).map(([level, data]) => 
    `- ${level}: ${safeValue(data.totalMarks)} marks (${safeToFixed(data.percentage)}%)`
  ).join('\n') || 'No taxonomy breakdown available'}`;
    };

    // Build topic comparison information (preserving existing)
    const buildTopicComparison = () => {
      if (!structureComparison) return 'Structure comparison not available';
      
      return `
  **Topic Marks Allocation Comparison:**
  ${Object.entries(structureComparison.topicComparison || {}).map(([topic, comp]) => 
    `- ${topic}: Task 1: ${safeValue(comp.task1Marks)} marks (${safeToFixed(comp.task1Percentage)}%) → Task 2: ${safeValue(comp.task2Marks)} marks (${safeToFixed(comp.task2Percentage)}%) | Change: ${safeValue(comp.marksDifference) >= 0 ? '+' : ''}${safeValue(comp.marksDifference)} marks`
  ).join('\n') || 'No topic comparison available'}

  **Subtopic Comparison:**
  ${Object.entries(structureComparison.subtopicComparison || {}).map(([subtopic, comp]) => 
    `- ${subtopic}: Task 1: ${safeValue(comp.task1Marks)} marks → Task 2: ${safeValue(comp.task2Marks)} marks | Change: ${safeValue(comp.marksDifference) >= 0 ? '+' : ''}${safeValue(comp.marksDifference)} marks`
  ).join('\n') || 'No subtopic comparison available'}

  **Concept Coverage Comparison:**
  ${Object.entries(structureComparison.conceptComparison || {}).map(([concept, comp]) => 
    `- ${concept}: Task 1: ${safeValue(comp.task1Marks)} marks → Task 2: ${safeValue(comp.task2Marks)} marks | Change: ${safeValue(comp.marksDifference) >= 0 ? '+' : ''}${safeValue(comp.marksDifference)} marks`
  ).join('\n') || 'No concept comparison available'}

  **Skills Assessment Comparison:**
  ${Object.entries(structureComparison.skillsComparison || {}).map(([skill, comp]) => 
    `- ${skill}: Task 1: ${safeValue(comp.task1Marks)} marks → Task 2: ${safeValue(comp.task2Marks)} marks | Change: ${safeValue(comp.marksDifference) >= 0 ? '+' : ''}${safeValue(comp.marksDifference)} marks`
  ).join('\n') || 'No skills comparison available'}

  **Difficulty Level Comparison:**
  ${Object.entries(structureComparison.difficultyComparison || {}).map(([level, comp]) => 
    `- ${level}: Task 1: ${safeValue(comp.task1Marks)} marks (${safeToFixed(comp.task1Percentage)}%) → Task 2: ${safeValue(comp.task2Marks)} marks (${safeToFixed(comp.task2Percentage)}%) | Change: ${safeValue(comp.marksDifference) >= 0 ? '+' : ''}${safeValue(comp.marksDifference)} marks`
  ).join('\n') || 'No difficulty comparison available'}`;
    };

    // Build performance analysis (preserving existing + enhancing)
    const buildPerformanceAnalysis = () => {
      if (scope === 'learner' && comparisonData?.topicImprovements) {
        return `
  **Individual Topic Performance Analysis (${comparisonData.learner || 'Unknown Learner'} - ${comparisonData.school || 'Unknown School'}):**
  ${Object.entries(comparisonData.topicImprovements).map(([topic, improvement]) => 
    `- ${topic}: ${safeToFixed(improvement.task1Performance)}% → ${safeToFixed(improvement.task2Performance)}% | Change: ${safeValue(improvement.improvement) >= 0 ? '+' : ''}${safeToFixed(improvement.improvement)}% (${safeToFixed(improvement.improvementPercentage)}%)`
  ).join('\n')}`;
      } else if (scope === 'grade' && topicPerformanceAnalysis?.task1GradeTopicPerformance) {
        return `
  **Grade Topic Performance Analysis:**
  ${Object.keys(topicPerformanceAnalysis.task1GradeTopicPerformance).map(topic => {
    const perf1 = safeValue(topicPerformanceAnalysis.task1GradeTopicPerformance[topic]);
    const perf2 = safeValue(topicPerformanceAnalysis.task2GradeTopicPerformance?.[topic]);
    const change = perf2 - perf1;
    return `- ${topic}: ${safeToFixed(perf1)}% → ${safeToFixed(perf2)}% | Change: ${change >= 0 ? '+' : ''}${safeToFixed(change)}%`;
  }).join('\n')}`;
      }
      return 'Topic performance analysis not available';
    };

    // Enhanced: Build school and learner performance sections
    const buildSchoolPerformanceSection = () => {
      if (scope === 'grade' && comparisonData?.bestPerformingSchools) {
        return `
  ## 🏆 TOP PERFORMING SCHOOLS
  ${comparisonData.bestPerformingSchools.map((school, index) => `
  ### ${index + 1}. ${school.schoolName || 'Unknown School'}
  - **Learners:** ${safeValue(school.learnerCount)}
  - **Average Improvement:** ${safeValue(school.averageImprovement) >= 0 ? '+' : ''}${safeToFixed(school.averageImprovement)}%
  - **Task 1 → Task 2:** ${safeToFixed(school.avgTask1Performance)}% → ${safeToFixed(school.avgTask2Performance)}%
  - **Performance Level:** ${school.performanceLevel || 'Not assessed'}
  - **Best Topic Improvement:** ${Object.entries(school.topicPerformance?.improvements || {}).sort((a, b) => safeValue(b[1]) - safeValue(a[1]))[0]?.[0] || 'N/A'}
  `).join('')}

  ## 🚨 SCHOOLS REQUIRING ATTENTION
  ${(comparisonData.schoolsNeedingAttention || []).map((school, index) => `
  ### ${index + 1}. ${school.schoolName || 'Unknown School'}
  - **Learners:** ${safeValue(school.learnerCount)}
  - **Performance Change:** ${safeValue(school.averageImprovement) >= 0 ? '+' : ''}${safeToFixed(school.averageImprovement)}%
  - **Task 1 → Task 2:** ${safeToFixed(school.avgTask1Performance)}% → ${safeToFixed(school.avgTask2Performance)}%
  - **Challenge Area:** ${Object.entries(school.topicPerformance?.improvements || {}).sort((a, b) => safeValue(a[1]) - safeValue(b[1]))[0]?.[0] || 'N/A'}
  - **Support Priority:** ${safeValue(school.averageImprovement) < -10 ? 'URGENT' : safeValue(school.averageImprovement) < 0 ? 'HIGH' : 'MODERATE'}
  `).join('')}

  ## 🌟 TOP PERFORMING LEARNERS
  ${(comparisonData.bestPerformingLearners || []).map((learner, index) => `
  ${index + 1}. **${learner.fullName || 'Unknown Learner'}** (${learner.school || 'Unknown School'})
    - Improvement: ${safeValue(learner.improvement) >= 0 ? '+' : ''}${safeToFixed(learner.improvement)}% (${safeToFixed(learner.task1Mark)}% → ${safeToFixed(learner.task2Mark)}%)
    - Level: ${learner.performanceLevel || 'Not assessed'} | Category: ${learner.improvementCategory || 'Not categorized'}
  `).join('')}

  ## 📢 LEARNERS REQUIRING ATTENTION
  ${(comparisonData.learnersNeedingAttention || []).map((learner, index) => `
  ${index + 1}. **${learner.fullName || 'Unknown Learner'}** (${learner.school || 'Unknown School'})
    - Performance Change: ${safeValue(learner.improvement) >= 0 ? '+' : ''}${safeToFixed(learner.improvement)}% (${safeToFixed(learner.task1Mark)}% → ${safeToFixed(learner.task2Mark)}%)
    - Level: ${learner.performanceLevel || 'Not assessed'} | Support Need: ${safeValue(learner.improvement) < -10 ? 'URGENT' : 'HIGH'}
  `).join('')}`;
      }
      return '';
    };

    // Safe access to comparison data properties
    const improvement = scope === 'learner' 
      ? safeValue(comparisonData?.improvement)
      : safeValue(comparisonData?.overallImprovement);
      
    const improvementPercentage = scope === 'learner'
      ? safeValue(comparisonData?.improvementPercentage)
      : safeValue(comparisonData?.overallImprovementPercentage);

    const totalLearners = scope === 'learner' 
      ? 1 
      : safeValue(comparisonData?.totalLearners);
      
    const totalSchools = scope === 'learner' 
      ? 1 
      : safeValue(comparisonData?.totalSchools);

    return `Generate a comprehensive task comparison analysis report with complete topic analysis and enhanced school/learner performance insights using markdown formatting.

  ## 📊 ENHANCED TASK COMPARISON ANALYSIS

  **Comparison Scope:** ${scope === 'learner' ? 'Individual Learner' : 'Grade Level'}
  ${scope === 'learner' ? `**Learner:** ${comparisonData?.learner || 'Unknown Learner'} (${comparisonData?.school || 'Unknown School'})` : ''}
  ${scope === 'grade' ? `**Total Learners:** ${totalLearners} | **Schools:** ${totalSchools}` : ''}

  ## 📋 DETAILED TASK ANALYSIS

  ### Task 1 Analysis
  ${buildTaskStructureInfo(task1, task1Structure)}

  ### Task 2 Analysis  
  ${buildTaskStructureInfo(task2, task2Structure)}

  ## 🔄 COMPREHENSIVE STRUCTURAL COMPARISON
  ${buildTopicComparison()}

  ## 📈 PERFORMANCE COMPARISON
  - **Overall Performance Change:** ${improvement >= 0 ? '+' : ''}${safeToFixed(improvement)}%
  - **Improvement Rate:** ${safeToFixed(improvementPercentage)}%
  ${scope === 'grade' ? `- **Pass Rate Change:** ${safeToFixed(comparisonData?.task1?.passRate)}% → ${safeToFixed(comparisonData?.task2?.passRate)}%` : ''}

  ## 🎯 TOPIC-LEVEL PERFORMANCE ANALYSIS
  ${buildPerformanceAnalysis()}

  ${buildSchoolPerformanceSection()}

  ${contextSection}

  Please provide a comprehensive analysis with markdown formatting including:

  # 1. 📊 Overall Assessment Comparison Summary
  # 2. 📚 Detailed Curriculum Coverage Analysis
  # 3. 🎯 Topic-by-Topic Performance Breakdown
  # 4. 🔍 Subtopic and Concept Analysis
  # 5. 🎨 Skills and Taxonomy Assessment
  # 6. 🏆 Celebrating Success (Schools & Learners)
  # 7. 🚨 Priority Intervention Areas
  # 8. 📈 Learning Progression Patterns
  # 9. 💡 Assessment Design Insights
  # 10. 🎯 Topic-Specific Teaching Strategies
  # 11. 🏫 School-Specific Recommendations
  # 12. 👥 Individual Learner Support Plans
  # 13. 🤝 Cross-School Collaboration Opportunities
  # 14. 📋 Comprehensive Action Plan

  Focus on actionable insights that integrate topic-level analysis with school and learner performance, celebrating achievements while providing clear strategies for improvement across all levels.`;
  }

  static generateComparisonChartData(comparisonData) {
    return {
      performance: {
        labels: [comparisonData.task1.title, comparisonData.task2.title],
        datasets: [{
          label: 'Performance (%)',
          data: [
            comparisonData.task1.mark || comparisonData.task1.averageMark,
            comparisonData.task2.mark || comparisonData.task2.averageMark
          ],
          backgroundColor: ['rgba(59, 130, 246, 0.7)', 'rgba(16, 185, 129, 0.7)'],
          borderColor: ['rgb(59, 130, 246)', 'rgb(16, 185, 129)'],
          borderWidth: 2
        }]
      }
    };
  }

  static generateEnhancedComparisonChartData(comparisonData, structureComparison, topicPerformanceAnalysis) {
    const charts = [];

    // Overall performance comparison
    charts.push({
      type: 'bar',
      title: 'Overall Performance Comparison',
      data: {
        labels: [comparisonData.task1.title, comparisonData.task2.title],
        datasets: [{
          label: 'Performance (%)',
          data: [
            comparisonData.task1.mark || comparisonData.task1.averageMark,
            comparisonData.task2.mark || comparisonData.task2.averageMark
          ],
          backgroundColor: [
            'rgba(59, 130, 246, 0.7)',
            comparisonData.improvement >= 0 ? 'rgba(16, 185, 129, 0.7)' : 'rgba(239, 68, 68, 0.7)'
          ]
        }]
      }
    });

    // Topic marks allocation comparison
    if (structureComparison.topicComparison) {
      const topicLabels = Object.keys(structureComparison.topicComparison);
      charts.push({
        type: 'bar',
        title: 'Topic Marks Allocation Comparison',
        data: {
          labels: topicLabels,
          datasets: [
            {
              label: 'Task 1 Marks',
              data: topicLabels.map(topic => structureComparison.topicComparison[topic].task1Marks),
              backgroundColor: 'rgba(59, 130, 246, 0.7)'
            },
            {
              label: 'Task 2 Marks',
              data: topicLabels.map(topic => structureComparison.topicComparison[topic].task2Marks),
              backgroundColor: 'rgba(16, 185, 129, 0.7)'
            }
          ]
        }
      });
    }

    // Topic performance comparison
    if (comparisonData.topicImprovements) {
      const topicLabels = Object.keys(comparisonData.topicImprovements);
      charts.push({
        type: 'radar',
        title: 'Topic Performance Comparison',
        data: {
          labels: topicLabels,
          datasets: [
            {
              label: 'Task 1 Performance',
              data: topicLabels.map(topic => comparisonData.topicImprovements[topic].task1Performance),
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              borderColor: 'rgb(59, 130, 246)'
            },
            {
              label: 'Task 2 Performance',
              data: topicLabels.map(topic => comparisonData.topicImprovements[topic].task2Performance),
              backgroundColor: 'rgba(16, 185, 129, 0.2)',
              borderColor: 'rgb(16, 185, 129)'
            }
          ]
        }
      });
    }

    // Difficulty level comparison
    if (structureComparison.difficultyComparison) {
      const difficultyLabels = Object.keys(structureComparison.difficultyComparison);
      charts.push({
        type: 'doughnut',
        title: 'Difficulty Level Distribution Comparison',
        data: {
          labels: difficultyLabels.map(level => `${level} - Task 1`).concat(difficultyLabels.map(level => `${level} - Task 2`)),
          datasets: [{
            data: difficultyLabels.map(level => structureComparison.difficultyComparison[level].task1Marks)
                   .concat(difficultyLabels.map(level => structureComparison.difficultyComparison[level].task2Marks)),
            backgroundColor: [
              'rgba(239, 68, 68, 0.7)',   // L1 - Task 1
              'rgba(249, 115, 22, 0.7)',  // L2 - Task 1  
              'rgba(234, 179, 8, 0.7)',   // L3 - Task 1
              'rgba(16, 185, 129, 0.7)',  // L4 - Task 1
              'rgba(239, 68, 68, 0.9)',   // L1 - Task 2
              'rgba(249, 115, 22, 0.9)',  // L2 - Task 2
              'rgba(234, 179, 8, 0.9)',   // L3 - Task 2
              'rgba(16, 185, 129, 0.9)',  // L4 - Task 2
            ]
          }]
        }
      });
    }

    return charts;
  }

  static getPerformanceLevel(percentage) {
  if (percentage >= 80) return 'Outstanding';
  if (percentage >= 70) return 'Excellent';
  if (percentage >= 60) return 'Good';
  if (percentage >= 50) return 'Adequate';
  if (percentage >= 40) return 'Developing';
  return 'Needs Support';
}

static getImprovementCategory(improvement) {
  if (improvement >= 20) return 'Significant Improvement';
  if (improvement >= 10) return 'Good Improvement';
  if (improvement >= 5) return 'Moderate Improvement';
  if (improvement >= 0) return 'Slight Improvement';
  if (improvement >= -5) return 'Slight Decline';
  if (improvement >= -10) return 'Moderate Decline';
  return 'Significant Decline';
}

static findCommonLearners(marks1, marks2) {
  const learners1 = new Map();
  const learners2 = new Map();
  
  marks1.forEach(mark => {
    const key = `${mark.learnerSurname}|${mark.learnerName}`;
    learners1.set(key, mark);
  });
  
  marks2.forEach(mark => {
    const key = `${mark.learnerSurname}|${mark.learnerName}`;
    learners2.set(key, mark);
  });
  
  const commonLearners = [];
  learners1.forEach((mark1, key) => {
    if (learners2.has(key)) {
      const mark2 = learners2.get(key);
      commonLearners.push({
        key,
        surname: mark1.learnerSurname,
        name: mark1.learnerName,
        fullName: `${mark1.learnerSurname}, ${mark1.learnerName}`,
        task1Mark: mark1.percentageMark,
        task2Mark: mark2.percentageMark,
        improvement: mark2.percentageMark - mark1.percentageMark,
        improvementPercentage: mark1.percentageMark > 0 ? 
          ((mark2.percentageMark - mark1.percentageMark) / mark1.percentageMark) * 100 : 0
      });
    }
  });
  
  return commonLearners;
}
}